// Fill-plan types — mirrors what the PJ Daily Solution writes to
// run.result_tables.mfg_and_fill_summary_long (one row per
// (Model Type, Sku, order); day-pivoted fills).
//
// Field names match the emitted column names verbatim (incl. spaces and the
// lowercase 'order') so raw rows can be read without a translation layer.
// Column contract: cplex_models/pj_daily_solution_20260609/mfg_fill_summary_long.py
// (MFG_FILL_SUMMARY_LONG_COLUMNS) and docs/integrations/pj-supply-chain.md §4.2.

export interface FillSummaryLongRow {
  'Model Type': string;
  Sku: string | null;
  order: string | null; // "Customer Name (Sales Order No)"
  Department: string | null;
  'Order Date': string | null;
  'Required By': string | null; // "YYYY-MM-DD" or "-"
  'MTO Status': string | null;
  Bulk: string | null;
  Container: string | null;
  'Filled From': string | null; // Madison Factory / Solon Warehouse / Dropped / Unfilled
  'Units on Order': number | null;
  'Units Shipped': number | null;
  'Fill Period (day 1)': number | null;
  'Critical PO': string | null; // 'Yes' | 'No' | '-'
  Materials: string | null;
  'PO Type': string | null;
  'PO Number': string | null;
  'PO Qty': string | null;
  'PO Landing Date': string | null;
  'day 1 fill': number | null;
  'day 2 fill': number | null;
  'day 3 fill': number | null;
  'day 4 fill': number | null;
  'day 5 fill': number | null;
  '5 day tot fill': number | null;
  'fg inv (000)': number | null;
  'fg inv (003)': number | null;
  'fg inv (other)': number | null;
  'fg inv': number | null;
  [key: string]: unknown;
}

/** One contributing order line rolled up into a plan row (drill-down only —
 * PJ 2026-06-26: "we do not need to show customers in the fill plan"). */
export interface ContributingOrder {
  order: string; // "Customer Name (Sales Order No)"
  units: number; // 5-day fill units this line contributes
  dayUnits: number[]; // index 1..5; [0] unused
  requiredBy: string | null;
  mtoStatus: string | null;
  criticalPo: boolean;
  score: number | null; // from the run's order_scores table (null = not found)
}

/** One row of the plan table: a finished good, day-pivoted.
 * Spec (Scott/PJ meetings 2026-06-26 + 06-29): aggregate by SKU; day 1..5 fill
 * columns; priority = MAX order score across contributing orders (not sum). */
export interface FillPlanSku {
  sku: string; // full label "Name (CODE)"
  name: string;
  code: string;
  department: string;
  dayUnits: number[]; // index 1..5; [0] unused
  totalUnits: number;
  orders: ContributingOrder[];
  maxScore: number | null; // MAX across orders with a known score
  fgInv: number;
  fgInvMadison: number;
  fgInvSolon: number;
  earliestRequiredBy: string | null;
  daysLate: number; // vs earliestRequiredBy; 0 when not late / no date
  criticalPo: boolean;
  poLandingDate: string | null;
  mtoStatus: string | null; // highest MTO tier seen
  fillPeriod: number | null; // min day-1 run order across lines (null = none)
}

export interface BusinessDay {
  n: number; // 1..5
  iso: string; // YYYY-MM-DD
  label: string; // "Mon Jul 6"
}

export type FillPlanSortKey = 'priority' | 'volume' | 'due' | 'sku';

export interface FillPlanFilterState {
  query: string;
  department: string; // '' = all
  day: number; // 0 = all, 1..5 = must have fill on that day
  sort: FillPlanSortKey;
}

export const EMPTY_FILL_PLAN_FILTERS: FillPlanFilterState = {
  query: '',
  department: '',
  day: 0,
  sort: 'priority',
};
